import { LockOutlined, MailOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Divider,
  Flex,
  Form,
  Input,
  Modal,
  Typography,
  message,
} from "antd";
import { jwtDecode } from "jwt-decode";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import "./Login.css";

export default function Login() {
  const [isLoading, setIsLoading] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [loginForm] = Form.useForm();
  const [resetForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = 0.5;
  }, []);

  const { login, requestPasswordReset } = useAuth();
  const navigate = useNavigate();

  const normalizeEmail = (email) => email.trim().toLowerCase();

  const handleLogin = (values) => {
    setIsLoading(true);

    messageApi.open({
      key: "login-status",
      type: "loading",
      content: "Efetuando login...",
    });

    login(normalizeEmail(values.email), values.password)
      .then((response) => {
        if (response.status === 200) {
          messageApi.open({
            key: "login-status",
            type: "success",
            content: "Login efetuado com sucesso!",
            duration: 2.5,
          });
          const accessToken = response?.data?.access;
          if (accessToken) {
            const decodedToken = jwtDecode(accessToken);
            if (decodedToken?.is_superuser) {
              navigate("/geral");
              return;
            }
          }
          navigate("/");
        } else {
          messageApi.open({
            key: "login-status",
            type: "error",
            content: "E-mail ou senha inválidos.",
          });
        }
      })
      .catch((error) => {
        console.error(error);
        const apiMessage =
          error?.response?.data?.detail ||
          error?.response?.data?.non_field_errors?.[0];
        messageApi.open({
          key: "login-status",
          type: "error",
          content: apiMessage || "Não foi possível concluir o login.",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleResetRequest = async () => {
    try {
      const values = await resetForm.validateFields();
      setIsSendingReset(true);
      await requestPasswordReset(normalizeEmail(values.email));
      messageApi.success(
        "Se o e-mail estiver cadastrado, enviaremos as instruções para redefinição de senha."
      );
      setIsResetModalOpen(false);
      resetForm.resetFields();
    } catch (error) {
      if (!error?.errorFields) {
        console.error(error);
        messageApi.error(
          "Não foi possível processar sua solicitação agora. Tente novamente."
        );
      }
    } finally {
      setIsSendingReset(false);
    }
  };

  return (
    <div className="login-page">
      {contextHolder}
      <video
        ref={videoRef}
        className="login-bg-video"
        autoPlay
        muted
        loop
        playsInline
        preload="auto">
        <source src="/videos/ouran-login-bg.mp4" type="video/mp4" />
      </video>
      <div className="login-bg-overlay" />

      <Flex justify="center" align="center" className="login-content">
        <Card className="login-card" bordered={false}>
          <Flex align="center" justify="center">
            <img
              src={"icons/logo_horizontal.svg"}
              alt="ouran logo"
              className="login-logo"
            />
          </Flex>
          <Typography.Title level={4} className="login-title">
            Bem-vindo ao ouran
          </Typography.Title>
          <Typography.Paragraph className="login-subtitle">
            Acesse a plataforma
          </Typography.Paragraph>

          <Divider className="login-divider" />

          <Form
            form={loginForm}
            layout="vertical"
            requiredMark={false}
            onFinish={handleLogin}
            autoComplete="on">
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: "Informe seu email." },
                { type: "email", message: "Informe um e-mail válido." },
              ]}>
              <Input
                prefix={<MailOutlined />}
                placeholder="seu.email@empresa.com"
                autoComplete="username"
                inputMode="email"
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="Senha"
              rules={[{ required: true, message: "Informe sua senha." }]}>
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Digite sua senha"
                autoComplete="current-password"
              />
            </Form.Item>

            <Flex
              justify="flex-start"
              align="center"
              className="login-actions-row">
              <Button
                type="link"
                className="login-reset-link"
                onClick={() => setIsResetModalOpen(true)}>
                Esqueci minha senha
              </Button>
            </Flex>

            <Button
              htmlType="submit"
              type="primary"
              className="login-submit-btn"
              loading={isLoading}>
              Entrar
            </Button>
          </Form>
        </Card>
      </Flex>

      <Modal
        title="Redefinir senha"
        open={isResetModalOpen}
        onCancel={() => {
          setIsResetModalOpen(false);
          resetForm.resetFields();
        }}
        onOk={handleResetRequest}
        okText="Enviar instruções"
        cancelText="Cancelar"
        confirmLoading={isSendingReset}
        destroyOnHidden>
        <Typography.Paragraph type="secondary">
          Informe seu e-mail de acesso. Se ele estiver cadastrado, enviaremos as
          instruções para redefinição de senha.
        </Typography.Paragraph>
        <Form
          form={resetForm}
          layout="vertical"
          requiredMark={false}
          autoComplete="on">
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Informe seu email." },
              { type: "email", message: "Informe um e-mail válido." },
            ]}>
            <Input
              prefix={<MailOutlined />}
              placeholder="seu.email@empresa.com"
              autoComplete="email"
              inputMode="email"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
